import { getUploadPolicy } from '../services/uploadPolicyService';
import { extensionOf, isBlockedFileName } from '../services/blockedExtensionService';

/**
 * 첨부 사전 차단 — 입력창(ChatWindow.addFiles)과 예약 모달(ScheduleComposerModal)이 공유한다.
 *
 * 판정은 차단 확장자 → 용량 → 개수 순서이고, 걸린 것만 빼고 나머지는 첨부한다(일부가 크다고
 * 전량 거부하지 않는다). 안내는 파일당이 아니라 집계해서 1~3줄로 낸다(8개가 초과하면 토스트가 8개 쌓인다).
 * 정책값은 모듈 스토어 동기 getter로 읽는다 — 호출부 useCallback deps를 비워 두기 위해서다
 * (입력창의 Tauri 드롭 리스너가 addFiles를 의존성으로 가진다). 최종 판정은 언제나 서버 multer다.
 */

// 차단 확장자 안내 — 서버(multer fileFilter)의 문구와 톤을 맞춘다.
export function blockedAttachmentMessage(names: string[]): string {
  if (names.length === 1) {
    return `보안 정책으로 ${extensionOf(names[0])} 파일은 첨부하지 못했습니다.`;
  }
  return `보안 정책으로 파일 ${names.length}개를 첨부하지 못했습니다.`;
}

// 토스트 한 줄에 긴 한글 파일명이 그대로 들어가면 넘친다.
export function truncateFileName(name: string, max = 24): string {
  return name.length > max ? `${name.slice(0, max)}…` : name;
}

export interface AttachmentScreenResult {
  /** 첨부해도 되는 파일 (입력 순서 유지) */
  accepted: File[];
  /** 사용자에게 보여 줄 오류 안내 (토스트 한 건씩) */
  messages: string[];
}

/**
 * @param currentCount 이미 붙어 있는 첨부 수 — 개수 상한은 합산이다
 */
export function screenAttachmentFiles(files: File[], currentCount: number): AttachmentScreenResult {
  const { maxFileSizeMB, maxFiles } = getUploadPolicy();
  const maxBytes = maxFileSizeMB * 1024 * 1024;

  // 1) 차단 확장자 — 목록을 아직 못 받았으면 전부 통과시키고 서버 판정에 맡긴다(fail-open)
  const allowed: File[] = [];
  const blocked: File[] = [];
  files.forEach(file => (isBlockedFileName(file.name) ? blocked : allowed).push(file));

  // 2) 용량 초과
  const sized: File[] = [];
  const tooLarge: File[] = [];
  allowed.forEach(file => (file.size > maxBytes ? tooLarge : sized).push(file));

  // 3) 개수 상한 — 이미 붙어 있는 첨부까지 합산
  const room = Math.max(0, maxFiles - currentCount);
  const accepted = sized.slice(0, room);
  const countOverflow = sized.length - accepted.length;

  const messages: string[] = [];
  const rest = accepted.length > 0 ? ` 나머지 ${accepted.length}개는 첨부했습니다.` : '';
  if (blocked.length > 0) {
    messages.push(blockedAttachmentMessage(blocked.map(f => f.name)) + rest);
  }
  if (tooLarge.length === 1) {
    messages.push(`${truncateFileName(tooLarge[0].name)}이 너무 커서 첨부하지 못했습니다. 최대 ${maxFileSizeMB}MB까지 첨부할 수 있습니다.${rest}`);
  } else if (tooLarge.length > 1) {
    messages.push(`파일 ${tooLarge.length}개가 너무 커서 첨부하지 못했습니다. 최대 ${maxFileSizeMB}MB까지 첨부할 수 있습니다.${rest}`);
  }
  if (countOverflow > 0) {
    messages.push(`첨부는 한 번에 ${maxFiles}개까지 보낼 수 있어 ${countOverflow}개를 제외했습니다.`);
  }
  return { accepted, messages };
}

// 붙여넣기로 허용하는 이미지 MIME (SVG는 서버 차단 확장자라 제외).
// ⚠ 이미지 첨부는 붙여넣기 판정의 '마지막' 단계다 — 엑셀은 셀을 복사할 때 비트맵(CF_DIB)도 함께 실어
//    Chromium·WebView2가 image/png 파일로 합성해 주므로, 텍스트보다 먼저 보면 표가 통째로 이미지가 된다.
//    그래서 호출부는 클립보드에 글자가 없을 때만 이 함수를 부른다.
export const PASTE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** 클립보드의 이미지 파일 (캡처 도구·이미지 단독 복사). 무의미한 'image.png' 이름은 새로 짓는다 */
export function pastedImageFiles(dt: DataTransfer): File[] {
  const items = Array.from(dt.items ?? []);
  const imageFiles: File[] = [];
  items.forEach((item, idx) => {
    if (item.kind !== 'file' || !PASTE_IMAGE_TYPES.includes(item.type)) return;
    const file = item.getAsFile();
    if (!file) return;
    const ext = item.type === 'image/jpeg' ? 'jpg' : item.type.split('/')[1];
    const name = file.name && file.name !== 'image.png'
      ? file.name
      : `pasted-${Date.now()}-${idx}.${ext}`;
    imageFiles.push(new File([file], name, { type: file.type }));
  });
  return imageFiles;
}
